/**
 * map.js — Leaflet bootstrap for the locations page (port of
 * components/ui/map.tsx usage in locations-client.tsx).
 *
 * Markup contract:
 *   <div data-map style="height:…">
 *     <script type="application/json" data-map-locations>
 *       {"center": [46.05, 14.5], "zoom": 13, "locations": [
 *         {"lat": 46.05, "lng": 14.5, "title": "…", "address": "…", "url": "…"}
 *       ]}
 *     </script>
 *   </div>
 *
 * Requires vendor-leaflet.js + vendor-leaflet.css (vendored).
 *
 * 2026-10-14 (theme 3.17.2):
 *  - Tiles moved from CARTO (basemaps.cartocdn.com, requires an API key since
 *    25 Sep 2026 — without it the map rendered "API KEY REQUIRED" placeholders)
 *    to the standard OpenStreetMap tile server, which needs no key. A CSS filter
 *    on the tile pane keeps the muted, light look of the previous CARTO
 *    "light_all" style. OSM attribution must stay visible on the map.
 *  - The map now fits all markers (fitBounds, max zoom 16) instead of using the
 *    fixed "zoom" from the JSON payload, so both Ljubljana shops are visible.
 *  - Popup markup uses <div> instead of <p>: theme.css resets
 *    `.leaflet-popup-content p` with `all: unset !important`, which rendered the
 *    title, address and link inline as one run-on line.
 */
(function () {
  "use strict";

  var TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
  var TILE_ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
  // Muted "light" look (close to the previous CARTO light_all basemap).
  var TILE_FILTER = "grayscale(0.9) brightness(1.06) contrast(0.92)";
  var FIT_PADDING = [56, 56];
  var FIT_MAX_ZOOM = 16;

  function initMap(root) {
    if (root.__map || typeof window.L === "undefined") return;
    const dataEl = root.querySelector("[data-map-locations]");
    if (!dataEl) return;
    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    const map = window.L.map(root, {
      scrollWheelZoom: false,
      zoomControl: true,
    }).setView(data.center || [46.0569, 14.5058], data.zoom || 13);
    root.__map = map;

    window.L.tileLayer(TILE_URL, {
      attribution: TILE_ATTRIBUTION,
      maxZoom: 19,
    }).addTo(map);

    const tilePane = map.getPane("tilePane");
    if (tilePane) {
      tilePane.style.filter = TILE_FILTER;
      tilePane.style.webkitFilter = TILE_FILTER;
    }

    const icon = window.L.divIcon({
      className: "leaflet-div-icon",
      html:
        '<div class="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white shadow-lg ring-2 ring-white"><svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.976.544l.062.029.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" clip-rule="evenodd"/></svg></div>',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
    });

    const points = [];
    (data.locations || []).forEach((loc) => {
      const lat = parseFloat(loc.lat);
      const lng = parseFloat(loc.lng);
      if (isNaN(lat) || isNaN(lng)) return;
      points.push([lat, lng]);
      const marker = window.L.marker([lat, lng], { icon: icon }).addTo(map);
      if (loc.title) {
        const popupHtml =
          '<div class="rounded-xl bg-white p-3 shadow-lg border border-primary/10 min-w-[180px]">' +
          '<div class="font-body-semibold text-sm text-tertiary">' +
          loc.title +
          "</div>" +
          (loc.address
            ? '<div class="mt-1 font-body text-xs text-secondary">' +
              loc.address +
              "</div>"
            : "") +
          (loc.url
            ? '<a class="mt-2 inline-block font-body-bold text-xs text-primary" href="' +
              loc.url +
              '" target="_blank" rel="noopener">→</a>'
            : "") +
          "</div>";
        marker.bindPopup(popupHtml, { closeButton: false });
      }
    });

    // Fit the view to every marker (both shops are ~350 m apart, so the
    // template's Slovenia-wide zoom would stack them into one pin).
    if (points.length > 1) {
      map.fitBounds(window.L.latLngBounds(points), {
        padding: FIT_PADDING,
        maxZoom: FIT_MAX_ZOOM,
      });
    } else if (points.length === 1) {
      map.setView(points[0], FIT_MAX_ZOOM);
    }
  }

  function scan(root) {
    (root || document).querySelectorAll("[data-map]").forEach(initMap);
  }

  window.themeUtils.onReady(() => scan());
  window.themeMaps = { scan: scan };
})();
